import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';

import { useBroadcastChannel } from '@vueuse/core';
import { BROADCAST_CHANNELS } from '~/constants/broadcastChannels';
import { deepToRaw } from '~/utils/deepToRaw';
import { hash } from 'ohash';

export type Preferences = {
	states: string[];
	cities: string[];
	propertyTypes: PropertyTypeEnum[];
	regions: string[];
	askingPrice: number;
};

type ReadonlyPreferences = {
	readonly [K in keyof Preferences]: Preferences[K] extends Array<infer U>
		? ReadonlyArray<U>
		: Preferences[K];
};

/**
 * Message structure for cross-tab communication
 */
interface BroadcastMessage {
	/** Message type for synchronization between tabs */
	type: 'SYNC_REQUEST' | 'UPDATE';
	/** Preferences data being shared */
	data: Preferences | {};
}

interface UsePreferencesCookieReturn {
	/** Reactive preferences data */
	preferences: Ref<Preferences | undefined>;
	/** Whether there are active preferences in the current session */
	hasPreferences: ComputedRef<boolean>;
	/** Hash of the preferences cookie */
	preferencesCookieHash: ComputedRef<string>;
	/** Get current preferences */
	getPreferences: () => ReadonlyPreferences;
	/** Update preferences and broadcast changes to other tabs */
	setPreferences: (preferences: Preferences) => void;
}

/**
 * Composable for managing user preferences with cross-tab synchronization.
 *
 * Features:
 * - Cookie persistence with 30-day expiration
 * - Cross-tab synchronization using BroadcastChannel
 * - Automatic data sync when new tabs open
 * - Reactive preferences data
 *
 * @example
 * ```ts
 * const { preferences, setPreferences, getPreferences, hasPreferences } = usePreferencesCookie();
 *
 * // Update preferences
 * setPreferences({
 *   cities: ['São Paulo'],
 *   propertyTypes: [PropertyTypeEnum.APARTMENT],
 *   regions: ['centro'],
 *   askingPrice: 500000
 * });
 *
 * // Access preferences data
 * const userCities = computed(() => preferences.value?.cities || []);
 * ```
 *
 * @returns Methods and reactive data for preferences management
 */
export function usePreferencesCookie(): UsePreferencesCookieReturn {
	const maxAge = 60 * 60 * 24 * 30;
	const preferencesCookie = useCookie<Preferences | undefined>('preferences', {
		maxAge,
		sameSite: true,
		expires: new Date(Date.now() + maxAge * 1000)
	});
	const preferences = computed({
		get(): Preferences | undefined {
			const value = preferencesCookie.value as Preferences | string | undefined;
			return value === '' ? undefined : (value as Preferences | undefined);
		},
		set(value: Preferences | undefined) {
			preferencesCookie.value = value;
		}
	});

	// Set up broadcast channel for cross-tab synchronization
	const {
		isSupported,
		post,
		data: preferencesChannelData
	} = useBroadcastChannel<BroadcastMessage, BroadcastMessage>({
		name: BROADCAST_CHANNELS.PREFERENCES_SYNC
	});

	// Handle cross-tab message synchronization
	watch(
		preferencesChannelData,
		newData => {
			if (!newData?.data) return;

			const messageHandlers = {
				SYNC_REQUEST: () => {
					if (preferencesCookie.value) {
						post({
							type: 'UPDATE',
							// deepToRaw: nested reactive arrays survive a shallow toRaw and
							// cannot be structured-cloned by BroadcastChannel → DataCloneError
							data: deepToRaw(preferencesCookie.value) as Preferences
						});
					}
				},
				UPDATE: () => {
					if (Object.keys(newData.data).length > 0) {
						preferencesCookie.value = { ...newData.data } as Preferences;
					}
				}
			};

			messageHandlers[newData.type]?.();
		},
		{ immediate: true }
	);

	// Request data sync when a new tab opens
	watch(
		isSupported,
		isReallySupported => {
			if (isReallySupported) {
				post({
					type: 'SYNC_REQUEST',
					data: {}
				});
			}
		},
		{ immediate: true }
	);

	const hasPreferences = computed<boolean>(
		() => !!preferences.value && Object.keys(preferences.value).length > 0
	);

	const readonlyPreferences = computed(
		() => readonly(preferences.value || {}) as ReadonlyPreferences
	);

	const preferencesCookieHash = computed(() => {
		return hash(preferences.value);
	});

	/**
	 * Get current preferences
	 * @returns Current preferences from cookie storage
	 */
	function getPreferences(): ReadonlyPreferences {
		return readonlyPreferences.value;
	}

	/**
	 * Update preferences and broadcast changes to other tabs
	 * @param preferences - New preferences to store
	 */
	function setPreferences(preferences: Preferences): void {
		preferencesCookie.value = {
			...preferences
		};

		// Broadcast changes to other tabs
		if (isSupported.value) {
			post({
				type: 'UPDATE',
				data: deepToRaw(preferences) as Preferences
			});
		}
	}

	return {
		preferences,
		hasPreferences,
		preferencesCookieHash,
		getPreferences,
		setPreferences
	};
}
